import { useEffect, useRef, useState } from 'react'
import { Images } from '../../modules/API/Images/images'
import { IImageObjectRef } from '../../modules/API/Images/models/imageObject'
import { IUploadedImage } from '../../modules/API/Images/models/imageUploadResult'

export interface UseObjectImagesReturn {
  images: IUploadedImage[]
  /** Последний комментарий объекта. Только для чтения. */
  comment: string
  isLoading: boolean
  /** true, если запрос не удался: блок просто не показываем. */
  hasError: boolean
}

const EMPTY: IUploadedImage[] = []

/**
 * Загружает фотографии и последний комментарий объекта. `null` в ref означает,
 * что объекта нет (например, у строки без теуды) — запрос не уходит вовсе.
 */
export const useObjectImages = (ref: IImageObjectRef | null): UseObjectImagesReturn => {
  const [images, setImages] = useState<IUploadedImage[]>(EMPTY)
  const [comment, setComment] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  // Поколение запроса: ответ прошлого ref или пришедший после размонтирования
  // не должен перезаписать состояние.
  const runIdRef = useRef<number>(0)

  const key = ref ? `${ref.ObjectType}:${ref.ObjectID}:${ref.ObjectSubID}` : null

  useEffect(() => {
    if (!ref) {
      setImages(EMPTY)
      setComment('')
      setIsLoading(false)
      setHasError(false)
      return
    }
    const runId = ++runIdRef.current
    setIsLoading(true)
    setHasError(false)

    Images.getImages(ref)
      .then((res) => {
        if (runIdRef.current !== runId) return
        setImages(res.Images ?? EMPTY)
        setComment(res.Comment ?? '')
        setIsLoading(false)
      })
      .catch(() => {
        if (runIdRef.current !== runId) return
        setImages(EMPTY)
        setComment('')
        setHasError(true)
        setIsLoading(false)
      })

    return () => {
      runIdRef.current += 1
    }
    // key пересобирается из полей ref: новый объект с теми же полями не должен перезапрашивать.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { images, comment, isLoading, hasError }
}

export default useObjectImages
